/**
 * 74.1 (DECISIONS 337): the system in the sky — a seeded, deterministic generator of a *plausible* system: a sun, rocky
 * worlds inside its frost line, giants and icy bodies outside it, moons as smaller bodies round them. The kind of each
 * body follows from its distance from the sun (not "one of each planet type"); the archetypes come from the classic
 * mapgen's own tables (BIOME_AFFINITY by star type × ORBITAL_BIOME_MODIFIERS by zone, PLANET_SIZE_WEIGHTS), so the
 * worlds the owner likes keep appearing — how often is `archetypeCensus`, checked over many seeds.
 *
 * Two scales live here on purpose (SCALE.md: the sky's scale is chosen apart from the physics'):
 *   - `radius` is the body's **world radius in game units**, what a battle on it would be built at (SIZE_CATEGORY_RADIUS);
 *   - `radiusKm` and `orbitAu` / `orbitKm` are the **sky's** numbers, Earth-equivalent (Earth 6,371 km, 1 AU), which
 *     only ever set a body's direction and angular size seen from another. Nothing here reaches the Match.
 *
 * `skyFrom` is the pure half of the drawing: where every other body and the sun stand in a viewer's sky, with the
 * realism dial (`SkyDial`) applied — distances compressed toward Stellaris's, angular sizes enlarged or capped.
 * Static: no spin, no orbits (74.2). Deterministic (dmath only): the same seed gives the same system on every engine.
 */
import { SeededRandom } from './random';
import { dsin, dcos, dasin } from './dmath';
import type { Vec3, PlanetArchetype, PlanetSizeCategory, StarType, OrbitalZone } from '../types';
import { STAR_TYPES, PLANET_SIZE_WEIGHTS, SIZE_CATEGORY_RADIUS, BIOME_AFFINITY, ORBITAL_BIOME_MODIFIERS } from '../constants';

export const AU_KM = 149_597_870;
export const SUN_RADIUS_KM = 695_700;
export const EARTH_RADIUS_KM = 6371;

/** A body's kind by where it formed: rocky inside the frost line; gas and ice giants and icy worlds beyond it. */
export type BodyKind = 'rocky' | 'gasGiant' | 'iceGiant' | 'icy';
/** How many bodies the dial asks for (the star's own `planetCountRange` is the classic number; `standard` is near it). */
export type BodyCount = 'few' | 'standard' | 'many';

export interface SystemBody {
  id: number;
  /** "Vega III", "Vega III b" */
  name: string;
  kind: BodyKind;
  /** null for a planet; the parent's id for a moon */
  parent: number | null;
  /** a planet's semi-major axis (AU-equivalent); a moon's is its parent's */
  orbitAu: number;
  /** a moon's distance from its parent (km); 0 for a planet */
  orbitKm: number;
  /** the body's place on its orbit (rad; static) and the orbit's tilt from the system's plane (rad) */
  angle: number;
  inclination: number;
  /** the sky's radius (km, Earth-equivalent) */
  radiusKm: number;
  /** the battle's radius (game units) — the world a fight on it would be built at; 0 for a giant (no ground to fight on) */
  radius: number;
  sizeCategory: PlanetSizeCategory | 'giant';
  /** the archetype a battle there would use; null for a giant */
  archetype: PlanetArchetype | null;
  /** the temperature zone the archetype was drawn in */
  zone: OrbitalZone;
  /** giants only: a ring, as multiples of the radius, and its tilt (rad) */
  ring: { inner: number; outer: number; tilt: number } | null;
  /** gas giants: how many cloud bands show */
  bands: number;
}

export interface StarSystem {
  seed: string;
  name: string;
  star: { type: StarType; radiusKm: number; luminosity: number; coreColor: string; coronaColor: string };
  /** the habitable zone (AU) and the frost line (AU) — what the kinds and archetypes were drawn against */
  habitableAu: [number, number];
  frostLineAu: number;
  /** belts (AU), where a wide gap left rubble */
  belts: number[];
  bodies: SystemBody[];
}

export interface SystemGenOptions {
  starType?: StarType;
  count?: BodyCount;
  /**
   * DECISIONS 340 (the owner: "upregulate probability of certain planet types seeding individually"): a weight per archetype
   * multiplying its chance in every draw (absent or 1: the plausible table as it is, so systems are unchanged at default).
   */
  archetypeWeights?: Partial<Record<PlanetArchetype, number>>;
}

/** Luminosity (Sol = 1) and radius (R☉) per star type: semi-plausible, tamed (a true blue giant's habitable zone is 100 AU out). */
export const STAR_PHYSICS: Record<StarType, { luminosity: number; radiusSun: number }> = {
  redDwarf: { luminosity: 0.04, radiusSun: 0.3 },
  orangeDwarf: { luminosity: 0.35, radiusSun: 0.75 },
  yellowDwarf: { luminosity: 1, radiusSun: 1 },
  blueGiant: { luminosity: 30, radiusSun: 6 },
  redGiant: { luminosity: 150, radiusSun: 60 },
};

/** The sky's radius (km) per size category: tiny a moon, small Mars, medium Venus to Earth, large and massive super-Earths. */
export const SKY_RADIUS_KM: Record<PlanetSizeCategory, [number, number]> = {
  tiny: [1200, 2400], small: [2400, 4000], medium: [4000, 7000], large: [7000, 10000], massive: [10000, 14000],
};
/** Giants' sky radii (km): gas giants Saturn to Jupiter and past; ice giants Neptune's class. */
export const GIANT_RADIUS_KM = { gasGiant: [40000, 75000] as [number, number], iceGiant: [20000, 30000] as [number, number] };

/** Which star a seed draws (no star type asked): the temperate dwarfs lead, so the verdant and snowy worlds the owner likes keep turning up. */
export const STAR_MIX: Record<StarType, number> = { redDwarf: 2, orangeDwarf: 3, yellowDwarf: 4, blueGiant: 1.5, redGiant: 1 };

export const BODY_COUNTS: Record<BodyCount, [number, number]> = { few: [3, 5], standard: [5, 9], many: [8, 13] };

const STAR_NAMES = ['Vega', 'Altair', 'Deneb', 'Mirach', 'Sadr', 'Alcor', 'Thuban', 'Rastaban', 'Kochab', 'Alderamin', 'Nashira', 'Tarazed', 'Sheliak', 'Zosma', 'Izar', 'Merak'];
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];
const LETTERS = 'abcdefgh';
const DEG = Math.PI / 180;

function pickWeighted<K extends string>(weights: Record<K, number>, rng: SeededRandom): K {
  const keys = Object.keys(weights) as K[];
  let total = 0;
  for (const k of keys) total += weights[k];
  let roll = rng.range(0, total);
  for (const k of keys) { roll -= weights[k]; if (roll <= 0) return k; }
  return keys[keys.length - 1];
}

/** The classic mapgen's own draw (mapgen.ts pickStarDrivenBiome), by the zone a body's temperature puts it in rather than its index. */
export function pickArchetype(starType: StarType, zone: OrbitalZone, rng: SeededRandom, boost: Partial<Record<PlanetArchetype, number>> = {}): PlanetArchetype {
  const aff = BIOME_AFFINITY[starType], mod = ORBITAL_BIOME_MODIFIERS[zone];
  const weights = {} as Record<PlanetArchetype, number>;
  let total = 0;
  for (const k of Object.keys(aff) as PlanetArchetype[]) { weights[k] = aff[k] * mod[k] * (boost[k] ?? 1); total += weights[k]; }
  if (total <= 0) return 'pallor';
  return pickWeighted(weights, rng);
}

/** A rocky body's size category by the star (PLANET_SIZE_WEIGHTS); an icy body's, shifted small. */
function pickSize(starType: StarType, icy: boolean, rng: SeededRandom): PlanetSizeCategory {
  const w = { ...PLANET_SIZE_WEIGHTS[starType] };
  if (icy) { w.tiny += 40; w.small += 20; w.large = Math.max(0, w.large - 10); w.massive = 0; }
  return pickWeighted(w, rng);
}

/** Where a body's orbit puts it against the habitable zone: inner (hot), middle (temperate), outer (cold). */
export function zoneOf(orbitAu: number, habitable: [number, number]): OrbitalZone {
  if (orbitAu < habitable[0]) return 'inner';
  if (orbitAu > habitable[1]) return 'outer';
  return 'middle';
}

/** Generate a system from a seed: deterministic, plausible (see the file's header). */
export function generateSystem(seed: string | number, opts: SystemGenOptions = {}): StarSystem {
  const rng = new SeededRandom(`${seed}-system`);
  const starType = opts.starType ?? pickWeighted(STAR_MIX, rng);
  const phys = STAR_PHYSICS[starType], st = STAR_TYPES[starType];
  const hz = Math.sqrt(phys.luminosity);                     // the habitable zone's centre (AU): flux like Earth's
  const habitable: [number, number] = [0.75 * hz, 1.6 * hz];
  const frost = 2.7 * hz;                                     // the frost line (Sol: 2.7 AU)
  const name = STAR_NAMES[rng.int(0, STAR_NAMES.length - 1)];
  const [cMin, cMax] = BODY_COUNTS[opts.count ?? 'standard'];
  const count = rng.int(cMin, cMax);
  const boost = opts.archetypeWeights ?? {};

  // orbits: a first one close in, each next 1.3–2 times further (tighter the more there are, so the last stays within ~100 hz)
  const orbits: number[] = [];
  let a = hz * rng.range(0.25, 0.5);
  const ratio: [number, number] = count <= 5 ? [1.5, 2.0] : count <= 9 ? [1.4, 1.85] : [1.3, 1.6];
  for (let i = 0; i < count; i++) { orbits.push(a); a *= rng.range(ratio[0], ratio[1]); }

  const bodies: SystemBody[] = [];
  const belts: number[] = [];
  let giantsPastFrost = 0;
  for (let i = 0; i < count; i++) {
    const orbitAu = orbits[i], zone = zoneOf(orbitAu, habitable);
    let kind: BodyKind;
    if (orbitAu < frost) kind = 'rocky';
    else {
      const roll = rng.next();
      const pGas = giantsPastFrost < 2 ? 0.6 : 0.25;            // the giants gather just past the frost line
      kind = roll < pGas ? 'gasGiant' : roll < pGas + 0.3 ? 'iceGiant' : 'icy';
      if (kind !== 'icy') giantsPastFrost++;
    }
    const id = bodies.length;
    const angle = rng.range(0, 2 * Math.PI), inclination = rng.range(-3, 3) * DEG;
    if (kind === 'gasGiant' || kind === 'iceGiant') {
      const [lo, hi] = GIANT_RADIUS_KM[kind], radiusKm = rng.range(lo, hi);
      const ring = kind === 'gasGiant' && rng.next() < 0.45 ? { inner: rng.range(1.3, 1.6), outer: rng.range(2.0, 2.6), tilt: rng.range(10, 30) * DEG } : null;
      bodies.push({ id, name: `${name} ${ROMAN[i]}`, kind, parent: null, orbitAu, orbitKm: 0, angle, inclination, radiusKm, radius: 0, sizeCategory: 'giant', archetype: null, zone, ring, bands: kind === 'gasGiant' ? rng.int(4, 9) : 0 });
      // moons: gas giants 2–5, ice giants 1–3, each a tiny or small world of the outer zone, close in (Io sits at 6 R)
      const n = kind === 'gasGiant' ? rng.int(2, 5) : rng.int(1, 3);
      let orbitR = rng.range(3.5, 6);
      for (let m = 0; m < n; m++) {
        const cat: PlanetSizeCategory = rng.next() < 0.65 ? 'tiny' : 'small';
        const moonZone: OrbitalZone = zone === 'middle' || rng.next() < 0.25 ? 'middle' : 'outer';   // (a quarter are heated by the tide, as Io and Europa)
        bodies.push(moon(bodies.length, `${name} ${ROMAN[i]} ${LETTERS[m]}`, id, radiusKm, orbitAu, orbitR * radiusKm, cat, moonZone, starType, rng, boost));
        orbitR *= rng.range(1.5, 2.1);
      }
    } else {
      const cat = pickSize(starType, kind === 'icy', rng);
      const [lo, hi] = SKY_RADIUS_KM[cat], radiusKm = rng.range(lo, hi);
      const [rlo, rhi] = SIZE_CATEGORY_RADIUS[cat], radius = rng.range(rlo, rhi);
      const archZone: OrbitalZone = kind === 'icy' ? 'outer' : zone;
      bodies.push({ id, name: `${name} ${ROMAN[i]}`, kind, parent: null, orbitAu, orbitKm: 0, angle, inclination, radiusKm, radius, sizeCategory: cat, archetype: pickArchetype(starType, archZone, rng, boost), zone: archZone, ring: null, bands: 0 });
      // a moon: medium worlds and up sometimes hold one, massive ones usually; a moon is a fraction of its parent (ours is 0.27)
      const pMoon = cat === 'tiny' ? 0 : cat === 'small' ? 0.15 : cat === 'medium' ? 0.35 : cat === 'large' ? 0.45 : 0.6;
      if (rng.next() < pMoon) {
        const moonCat: PlanetSizeCategory = cat === 'massive' && rng.next() < 0.4 ? 'small' : 'tiny';
        bodies.push(moon(bodies.length, `${name} ${ROMAN[i]} a`, id, radiusKm, orbitAu, radiusKm * rng.range(25, 60), moonCat, archZone, starType, rng, boost));
      }
    }
    // a belt where the gap to the next orbit is wide (past the inner edge), sometimes
    if (i + 1 < count && orbits[i + 1] / orbitAu > 1.8 && orbitAu > habitable[0] * 0.5 && rng.next() < 0.5) belts.push(Math.sqrt(orbitAu * orbits[i + 1]));
  }
  return {
    seed: String(seed), name,
    star: { type: starType, radiusKm: phys.radiusSun * SUN_RADIUS_KM, luminosity: phys.luminosity, coreColor: st.coreColor, coronaColor: st.coronaColor },
    habitableAu: habitable, frostLineAu: frost, belts, bodies,
  };
}

function moon(id: number, name: string, parent: number, parentRadiusKm: number, orbitAu: number, orbitKm: number, cat: PlanetSizeCategory, zone: OrbitalZone, starType: StarType, rng: SeededRandom, boost: Partial<Record<PlanetArchetype, number>> = {}): SystemBody {
  const [lo, hi] = SKY_RADIUS_KM[cat], [rlo, rhi] = SIZE_CATEGORY_RADIUS[cat];
  return {
    id, name, kind: 'rocky', parent, orbitAu, orbitKm, angle: rng.range(0, 2 * Math.PI), inclination: rng.range(-5, 5) * DEG,
    radiusKm: Math.min(rng.range(lo, hi), parentRadiusKm * 0.35),   // (a moon is a fraction of its parent: ours is 0.27)
    radius: rng.range(rlo, rhi), sizeCategory: cat, archetype: pickArchetype(starType, zone, rng, boost), zone, ring: null, bands: 0,
  };
}

/** The bodies a battle can be fought on: every rocky or icy world and moon (a giant has no ground). */
export function battleWorlds(system: StarSystem): SystemBody[] {
  return system.bodies.filter(b => b.archetype !== null);
}

/** How many seeded systems `systemForWorld` tries before it falls back. */
/** The size category a radius falls in (SIZE_CATEGORY_RADIUS's upper bounds; past the last, massive). Moved here from rendering/systemSkyPlay.ts for 74.11 (the match's hour in core). */
export function sizeCategoryOf(radius: number): PlanetSizeCategory {
  for (const [cat, [, hi]] of Object.entries(SIZE_CATEGORY_RADIUS) as Array<[PlanetSizeCategory, [number, number]]>) if (radius <= hi) return cat;
  return 'massive';
}

export const SYSTEM_SEARCH_CAP = 32;
const SIZE_ORDER: PlanetSizeCategory[] = ['tiny', 'small', 'medium', 'large', 'massive'];

/**
 * The system a battle world belongs to (the 74.1 build; the coordinator, Oct 29: never relabel a body — a snow world drawn
 * sunward of a desert one is what the owner notices). Searches `${seed}-sys-${k}` for k = 0, 1, 2, … until a generated
 * system holds a battle world of `archetype`, taking the one nearest `sizeCategory`; past the cap it falls back to the first
 * system's largest battle world, drawn as its own archetype. Deterministic; the census says about half the systems lack a
 * given archetype, so the expected k is small (tests/systemGen.test.ts bounds it).
 */
export function systemForWorld(seed: string | number, archetype: PlanetArchetype, sizeCategory: PlanetSizeCategory, opts: SystemGenOptions = {}): { system: StarSystem; body: SystemBody; tries: number; exact: boolean } {
  const want = SIZE_ORDER.indexOf(sizeCategory);
  let first: StarSystem | null = null;
  for (let k = 0; k < SYSTEM_SEARCH_CAP; k++) {
    const system = generateSystem(`${seed}-sys-${k}`, opts);
    first ??= system;
    let best: SystemBody | null = null, bestGap = Infinity;
    for (const b of battleWorlds(system)) {
      if (b.archetype !== archetype) continue;
      const gap = Math.abs(SIZE_ORDER.indexOf(b.sizeCategory as PlanetSizeCategory) - want);
      if (gap < bestGap) { best = b; bestGap = gap; }
    }
    if (best) return { system, body: best, tries: k + 1, exact: true };
  }
  const worlds = battleWorlds(first!);
  let largest = worlds[0];
  for (const b of worlds) if (b.radiusKm > largest.radiusKm) largest = b;
  return { system: first!, body: largest, tries: SYSTEM_SEARCH_CAP, exact: false };
}

// ---- the sky from a body --------------------------------------------------------------------------------------------------

/**
 * The realism dial — the sky's scale, apart from the physics'. `distance` scales every planet's orbit about the sun (1 true;
 * 1/30 Stellaris-like: neighbours a few million km off); moons keep their true distance from their parent, and the sun keeps
 * its true angular size (`skyFrom`). `size` multiplies every body's angular radius (1 true), `maxDeg` caps the drawn
 * diameter; `sunSize` multiplies the sun's.
 */
export interface SkyDial { distance: number; size: number; maxDeg: number; sunSize: number }
export const SKY_DIALS = {
  true: { name: 'true: real distances, real sizes', distance: 1, size: 1, maxDeg: 180, sunSize: 1 },
  enlarged: { name: 'enlarged: real distances, bodies ×12 (to 8°), the sun ×2', distance: 1, size: 12, maxDeg: 8, sunSize: 2 },
  stellaris: { name: 'Stellaris: distances ÷30, real sizes', distance: 1 / 30, size: 1, maxDeg: 180, sunSize: 1 },
  stellarisPlus: { name: 'Stellaris+: distances ÷30, bodies ×4 (to 14°), the sun ×2', distance: 1 / 30, size: 4, maxDeg: 14, sunSize: 2 },
} as const satisfies Record<string, SkyDial & { name: string }>;
export type SkyDialKey = keyof typeof SKY_DIALS;
/** DECISIONS 340: the owner's pick — Stellaris+ by default. */
export const DEFAULT_SKY_DIAL: SkyDialKey = 'stellarisPlus';

/** A body's place in the system's frame (km; the sun at the origin; y the system plane's normal), with the dial's distances. */
export function bodyPosition(system: StarSystem, body: SystemBody, dial: SkyDial, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  if (body.parent === null) {
    const r = body.orbitAu * AU_KM * dial.distance;
    out.x = r * dcos(body.angle); out.y = r * dsin(body.angle) * dsin(body.inclination); out.z = r * dsin(body.angle) * dcos(body.inclination);
    return out;
  }
  const p = bodyPosition(system, system.bodies[body.parent], dial, out);
  const r = body.orbitKm;
  p.x += r * dcos(body.angle); p.y += r * dsin(body.angle) * dsin(body.inclination); p.z += r * dsin(body.angle) * dcos(body.inclination);
  return p;
}

/** One thing in a viewer's sky: its direction (unit, system frame), its distance, its true and drawn angular radii, and the sun's direction from it. */
export interface SkyView {
  body: SystemBody | null;   // null: the sun
  dir: Vec3;
  distanceKm: number;
  /** the angular radius as it truly is, and as the dial draws it (rad) */
  trueRadius: number;
  drawnRadius: number;
  /** the direction from the body to the sun (unit; the lit side) — for the sun itself, zero */
  sunDir: Vec3;
  /** the sun's flux at the body (Earth = 1): what the lit face is lit by */
  flux: number;
}
export interface SkyFromBody { sun: SkyView; bodies: SkyView[]; eye: Vec3 }

const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const len = (a: Vec3): number => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
const unit = (a: Vec3): Vec3 => { const l = len(a) || 1; return { x: a.x / l, y: a.y / l, z: a.z / l }; };

/**
 * The sky of a viewer standing on `on`, at the point of its surface whose outward normal is `up` (unit, system frame):
 * every other body and the sun, with the dial applied. Pure; the drawing turns `dir` into the world and places an impostor.
 */
export function skyFrom(system: StarSystem, on: SystemBody, up: Vec3, dial: SkyDial): SkyFromBody {
  const here = bodyPosition(system, on, dial);
  const eye: Vec3 = { x: here.x + up.x * on.radiusKm, y: here.y + up.y * on.radiusKm, z: here.z + up.z * on.radiusKm };
  const angular = (radiusKm: number, d: number, scale: number, maxDeg: number): number => {
    const t = dasin(Math.min(1, radiusKm / Math.max(radiusKm, d)));
    return Math.min(t * scale, maxDeg * DEG / 2);
  };
  // the sun: its direction from the eye; its size at the body's *true* distance (the dial's `distance` draws the planets nearer
  // each other, not the sun nearer — a sun thirty times closer would be sixteen degrees across and white out every day)
  const toSun = sub({ x: 0, y: 0, z: 0 }, eye);
  const hereTrue = bodyPosition(system, on, SKY_DIALS.true), dSun = len(hereTrue);
  const sun: SkyView = { body: null, dir: unit(toSun), distanceKm: dSun, trueRadius: angular(system.star.radiusKm, dSun, 1, 180), drawnRadius: angular(system.star.radiusKm, dSun, dial.sunSize, 180), sunDir: { x: 0, y: 0, z: 0 }, flux: 0 };
  const bodies: SkyView[] = [];
  for (const b of system.bodies) {
    if (b.id === on.id) continue;   // (by id: the clock's time-shifted system (skyClock.ts systemAt) holds fresh body objects — by identity the viewer's own world crept into its own sky, hidden only by the horizon rule, and showed from the system stop; 74.9)
    const p = bodyPosition(system, b, dial), rel = sub(p, eye), d = len(rel);
    const trueAu = Math.max(1e-3, len(bodyPosition(system, b, SKY_DIALS.true)) / AU_KM);
    bodies.push({ body: b, dir: unit(rel), distanceKm: d, trueRadius: angular(b.radiusKm, d, 1, 180), drawnRadius: angular(b.radiusKm, d, dial.size, dial.maxDeg), sunDir: unit(sub({ x: 0, y: 0, z: 0 }, p)), flux: system.star.luminosity / (trueAu * trueAu) });
  }
  return { sun, bodies, eye };
}

/** A surface normal on `on` where the sun stands at `elevation` (rad) over the horizon: the time of day, without a clock (74.2). */
export function upForSunElevation(system: StarSystem, on: SystemBody, elevation: number, dial: SkyDial, azimuthSeed = 0): Vec3 {
  const here = bodyPosition(system, on, dial), s = unit(sub({ x: 0, y: 0, z: 0 }, here));
  // a perpendicular to s, turned by the seed about s
  const ref: Vec3 = Math.abs(s.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const p1 = unit({ x: s.y * ref.z - s.z * ref.y, y: s.z * ref.x - s.x * ref.z, z: s.x * ref.y - s.y * ref.x });
  const p2 = { x: s.y * p1.z - s.z * p1.y, y: s.z * p1.x - s.x * p1.z, z: s.x * p1.y - s.y * p1.x };
  const c = dcos(azimuthSeed), sn = dsin(azimuthSeed);
  const p: Vec3 = { x: p1.x * c + p2.x * sn, y: p1.y * c + p2.y * sn, z: p1.z * c + p2.z * sn };
  const se = dsin(elevation), ce = dcos(elevation);
  return { x: s.x * se + p.x * ce, y: s.y * se + p.y * ce, z: s.z * se + p.z * ce };
}

// ---- the census -------------------------------------------------------------------------------------------------------------

export interface Census {
  seeds: number;
  /** battle worlds per archetype, over every seed */
  archetypes: Record<PlanetArchetype, number>;
  /** systems in which the archetype appears at least once */
  systemsWith: Record<PlanetArchetype, number>;
  kinds: Record<BodyKind, number>;
  stars: Record<StarType, number>;
  moons: number;
  battleWorlds: number;
  bodies: number;
  giantsWithMoons: number;
  ringed: number;
  /** battle worlds that are moons of a giant (the giant fills their sky) */
  giantMoons: number;
}

const ARCHETYPES: PlanetArchetype[] = ['thalia', 'bruma', 'tephra', 'murex', 'pallor', 'pelago', 'ochrus', 'viridis', 'sonora'];

/** How often each archetype, kind and star turns up over `seeds` systems (the plausibility check the owner asked for). */
export function archetypeCensus(seeds: number, opts: SystemGenOptions = {}, firstSeed = 1): Census {
  const zero = () => Object.fromEntries(ARCHETYPES.map(a => [a, 0])) as Record<PlanetArchetype, number>;
  const c: Census = {
    seeds, archetypes: zero(), systemsWith: zero(), kinds: { rocky: 0, gasGiant: 0, iceGiant: 0, icy: 0 },
    stars: { redDwarf: 0, orangeDwarf: 0, yellowDwarf: 0, blueGiant: 0, redGiant: 0 }, moons: 0, battleWorlds: 0, bodies: 0, giantsWithMoons: 0, ringed: 0, giantMoons: 0,
  };
  for (let s = 0; s < seeds; s++) {
    const sys = generateSystem(firstSeed + s, opts);
    c.stars[sys.star.type]++;
    const seen = new Set<PlanetArchetype>();
    for (const b of sys.bodies) {
      c.bodies++;
      if (b.parent === null) c.kinds[b.kind]++; else c.moons++;
      if (b.ring) c.ringed++;
      if (b.parent === null && (b.kind === 'gasGiant' || b.kind === 'iceGiant') && sys.bodies.some(m => m.parent === b.id)) c.giantsWithMoons++;
      if (b.archetype) {
        c.battleWorlds++; c.archetypes[b.archetype]++; seen.add(b.archetype);
        if (b.parent !== null && sys.bodies[b.parent].kind !== 'rocky') c.giantMoons++;
      }
    }
    for (const a of seen) c.systemsWith[a]++;
  }
  return c;
}
